import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Title from '../../components/Title';
import SignupForm from '../../components/SignupForm';
import Button, { buttonType } from '../../components/Button';
import OrgForm from './orgForm';
import OuterLayer from '../../containers/OuterLayer';
import { useLabels } from '../../hooks';
import { PageContext, DeviceContext, UserContext, UserType, OutersContainerContext, LanguageContext } from '../../contexts';
import { useSubscriptionApi } from '../../api';
import { scrollTop } from '../../utils/helpers';
import { env } from '../../config';
import styles from './Purchase.module.scss';
import IframePayment from '../payment/index';
import Loader from '../../components/Loader';

const pageKey = "purchase";

const subscriptionType = {
  corporate: 'corporate',
  monthly: 'monthly',
  yearly: 'yearly',
};

const generateSubscriptionUrl = (sku) => {
  return sku ? sku.toLowerCase().replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '') : '';
};

const Home = () => {
  const [subscription, setSubscription] = useState();
  const [subscriptionsData, setSubscriptionsData] = useState();
  const { getLabel, getLocalUrlByLanguage } = useLabels();
  const { setPageKey } = useContext(PageContext);
  const { isMobileView } = useContext(DeviceContext);
  const { hasPermissions, userType, orgData, codeDeliveryDetails, userData, dateUserExpired } = useContext(UserContext);
  const { appLayoutTitleContainer } = useContext(OutersContainerContext);
  const { getSubscriptions } = useSubscriptionApi();
  const navigate = useNavigate();
  const { subscriptionName } = useParams();
  const [loading, setLoading] = useState(true);
  const { language } = useContext(LanguageContext);
  const [showLoader, setShowLoader] = useState(true);
  const [isLetPurchase, setIsLetPurchase] = useState(false);

  useEffect(() => {

    // Redirect certain user types away from purchase, but allow inactive orgs to renew
    if (userType === UserType.external || userType === UserType.edu) {
      if (hasPermissions) {
        navigate(getLocalUrlByLanguage(`/`));
      }
    }

    if (dateUserExpired) {
      const expiry = new Date(dateUserExpired);
      const now = new Date();
      const diffDays = (now - expiry) / (1000 * 60 * 60 * 24);
      if (expiry > now && diffDays < -14) {
        navigate(getLocalUrlByLanguage(`/`));
      }
      else {
        setIsLetPurchase(true);
      }
    }
  }, [hasPermissions, getLocalUrlByLanguage, navigate, dateUserExpired, userType]);

  useEffect(() => {
    setPageKey(pageKey);
  }, [setPageKey]);

  useEffect(() => {
    setLoading(true);

    getSubscriptions()
      .then(response => {
        if (response.success && response.result.subscriptions) {
          let filteredSubscriptions;

          if (subscriptionName) {
            const normalizeString = (str) => str.toLowerCase().replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '');
            const specificSubscription = response.result.subscriptions.find(sub =>
              sub.SKU_priority && normalizeString(sub.SKU_priority) === normalizeString(subscriptionName)
            );

            filteredSubscriptions = specificSubscription ? [specificSubscription] : [];
          } else {
            filteredSubscriptions = response.result.subscriptions.filter(sub => sub.isActive);
          }

          const sortedSubscriptions = filteredSubscriptions.sort((a, b) =>
            (b.SKU_priority || 0) - (a.SKU_priority || 0)
          );

          setSubscriptionsData(sortedSubscriptions);
        }
      })
      .catch(error => {
        console.error('Failed to fetch subscriptions:', error);
      })
      .finally(() => {
        setLoading(false);
        setShowLoader(false);
      });
  }, [getSubscriptions, subscriptionName]);

  const corporate = useMemo(() => {
    return <OrgForm />;
  }, []);

  const signup = useMemo(() => {
    return <div className={`${styles.container} ${styles.container_signup}`}>
      <div className={styles.sign_up}>
        <OuterLayer container={appLayoutTitleContainer} TagWrapper='span'>{getLabel(env.REACT_APP_ORG_ENABLE() ? 'purchase_page_signup_org_title' : 'purchase_page_signup_title')}</OuterLayer>
        <SignupForm />
      </div>
    </div>;
  }, [appLayoutTitleContainer, getLabel]);

  const renderSubscriptionName = (nameText) => {
    if (!nameText) return null;

    const dashIndex = nameText.indexOf(' - ');
    if (dashIndex === -1) {
      return (
        <span className="block w-full overflow-hidden break-words">
          {nameText}
        </span>
      );
    }

    const firstPart = nameText.substring(0, dashIndex).trim();
    const secondPart = nameText.substring(dashIndex + 3).trim();

    const getSecondPartFontSize = (text) => {
      const length = text.length;
      if (length <= 10) return '0.5em';
      if (length <= 20) return '0.45em';
      if (length <= 30) return '0.4em';
      return '0.35em';
    };

    return (
      <span className="block w-full overflow-hidden break-words">
        {firstPart}
        <br />
        <small style={{
          fontSize: getSecondPartFontSize(secondPart),
          lineHeight: '1.2',
          display: 'block',
          marginTop: '0.2em'
        }}>
          {secondPart}
        </small>
      </span>
    );
  };

  const renderSubscription = (sub, isHighlighted = false) => {
    if (!sub) return null;

    let priceNumber = sub.price;
    let shekelIcon = language === 'he' ? <span>₪</span> : <span>ILS</span>;
    let perMonthText = language === 'he' ? 'לחודש' : 'per month';

    const isMonthly = sub.type?.name === 'monthly';
    const nameText = language === 'en' ? sub.nameEn : sub.name;

    return (
      <div className={`${styles.subscription} ${isHighlighted ? styles.highlight : ''}`}>
        {isHighlighted && (
          <div className={styles.subscription_label} data-label={getLabel('purchase_page_subscription_yearly_label')}></div>
        )}
        <Title className={styles.title} TagWrapper="div">
          {renderSubscriptionName(nameText)}
        </Title>
        <div className={styles.subscription_content}>
          <div className={`${styles.subscription_text} overflow-hidden break-words`}>
            <span className="block w-full overflow-hidden break-words">
              {language === 'en' ? sub.descriptionEn : sub.description}
            </span>
          </div>
          <div className={`${styles.subscription_price} overflow-hidden break-words`}>
            {priceNumber} {shekelIcon} {isMonthly && <span>{perMonthText}</span>}
          </div>
          <div className={`${styles.subscription_text} overflow-hidden break-words`}>
            {isMonthly ?
              getLabel('purchase_page_subscription_monthly_text') :
              getLabel('purchase_page_subscription_yearly_text')
            }
          </div>
        </div>
        <div className={styles.subscription_buttons}>
          <Button
            type={buttonType.premium}
            className={styles.subscription_button}
            onClick={() => { setSubscription(sub) }}

          >
            {getLabel(isLetPurchase ? 'purchase_page_subscription_renew_button' : 'purchase_page_subscription_buy_button')}
          </Button>
        </div>
      </div>
    );
  };

  const subscriptionSelector = useMemo(() => {
    if (subscriptionName && subscriptionsData && subscriptionsData.length === 1) {
      const specificSubscription = subscriptionsData[0];
      return (
        <div className={`${styles.container} ${styles.container_subscriptions}`}>
          <div className={`${styles.subscriptions} ${styles.single_subscription}`}>
            {renderSubscription(specificSubscription, specificSubscription.type?.name === 'yearly')}
          </div>
        </div>
      );
    }

    let monthly, yearly;
    if (subscriptionsData && subscriptionsData.length > 0) {
      monthly = subscriptionsData.find(sub => sub.type.name === 'monthly');
      yearly = subscriptionsData.find(sub => sub.type.name === 'yearly');
    }

    let yearlySubscription = null, monthlySubscription = null, orglySubscription = null;

    monthlySubscription = renderSubscription(monthly, false);
    yearlySubscription = renderSubscription(yearly, true);

    if (monthly && yearly) {
      orglySubscription = (
        <div className={styles.subscription}>
          <Title className={styles.title} TagWrapper="div">
            <span className="block w-full overflow-hidden break-words">{getLabel('purchase_page_subscription_corporate_title')}</span>
          </Title>
          <div className={styles.subscription_content}>
            <div className={`${styles.subscription_text} overflow-hidden break-words`}>
              <span>{getLabel('purchase_page_subscription_corporate_text')}</span>
            </div>
          </div>
          <div className={styles.subscription_buttons}>
            <Button
              type={buttonType.premium}
              className={styles.subscription_button}
              onClick={() => { setSubscription('corporate') }}

            >
              {getLabel('purchase_page_subscription_corporate_button')}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={`${styles.container} ${styles.container_subscriptions}`}>
        <div className={styles.subscriptions}>
          {monthlySubscription}
          {yearlySubscription}
          {orglySubscription}
        </div>
      </div>
    );
  }, [getLabel, subscriptionsData, language, navigate, isMobileView, isLetPurchase, subscriptionName]);

  const layout = useMemo(() => {
    if (env.REACT_APP_ORG_ENABLE() && ((orgData && orgData.isActive) || codeDeliveryDetails)) {
      return signup;
    }

    if (env.REACT_APP_ORG_ENABLE() || subscription === 'corporate') {
      return corporate;
    }

    if (!subscription) {
      return subscriptionSelector;
    }

    if (userType === UserType.guest) {
      return signup;
    }

    if (userType === UserType.privet && userData?.sub)
      return <IframePayment subscription={subscription} isLetPurchase={isLetPurchase}></IframePayment>;
  }, [codeDeliveryDetails, corporate, orgData, signup, subscription, subscriptionSelector, userType, userData, isMobileView, isLetPurchase]);

  return <>{layout}{showLoader && <Loader />}</>;
};

export default Home;
